import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit"

import * as receiptsApi from "../api/services.ts"
import { type Receipt } from "../api/services.types.ts"

interface ReceiptsState {
  activeReceipt: Receipt | null
  inbox: Receipt[]
  inboxCount: number
  status: "idle" | "loading" | "failed"
}

const initialState: ReceiptsState = {
  activeReceipt: null,
  inbox: [],
  inboxCount: 0,
  status: "idle"
}

export const uploadReceiptThunk = createAsyncThunk(
  "receipts/upload",
  async (args: { file: File; projectId?: string }) =>
    receiptsApi.uploadReceipt(args)
)

export const fetchReceipt = createAsyncThunk(
  "receipts/fetch",
  async (id: string) => receiptsApi.getReceipt(id)
)

export const fetchInbox = createAsyncThunk(
  "receipts/fetchInbox",
  async (params?: Record<string, string>) => receiptsApi.listReceipts(params)
)

const receiptsSlice = createSlice({
  name: "receipts",
  initialState,
  reducers: {
    // Used when the Inbox links into a receipt whose current state it
    // already fetched, so the Review screen isn't blank while its own
    // fetch resolves — and by this slice's own test.
    setActiveReceiptForTest(state, action: PayloadAction<Receipt>) {
      state.activeReceipt = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadReceiptThunk.fulfilled, (state, action) => {
        state.activeReceipt = action.payload
      })
      .addCase(fetchReceipt.fulfilled, (state, action) => {
        state.activeReceipt = action.payload
      })
      .addCase(fetchInbox.pending, (state) => {
        state.status = "loading"
      })
      .addCase(fetchInbox.fulfilled, (state, action) => {
        state.inbox = action.payload.results
        state.inboxCount = action.payload.count
        state.status = "idle"
      })
      .addCase(fetchInbox.rejected, (state) => {
        state.status = "failed"
      })
  }
})

export const { setActiveReceiptForTest } = receiptsSlice.actions
export default receiptsSlice.reducer
